import { Box } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import moment                                                                                from 'moment-timezone';
import React, { ForwardRefRenderFunction, ReactNode, forwardRef, memo, useEffect, useState } from 'react';
import { ReactComponent as IconDate }                                                        from '../../../../images/icons/icon-date.svg';
import {
  ITextInputOwnProps,
  TextInput,
}                                                                                            from '../../inputs/TextInput/TextInput';
import {
  dateRegex,
  getMoment,
}                                                                                            from '../../utils/commonFunctions';

export interface ISelectDateProps
  extends ITextInputOwnProps {
  id?: string;
  label: ReactNode;
  name?: string;
  value?: string | null;
  minDate?: string;
  maxDate?: string;
  error?: boolean;
  helperText?: ReactNode;
  shrink?: boolean;
  disabled?: boolean;
  className?: string;
  onChange?: (event: unknown) => void;
}

const SelectDateBase: ForwardRefRenderFunction<HTMLDivElement, ISelectDateProps> = ({
  name,
  label,
  error,
  value,
  minDate,
  maxDate,
  shrink = true,
  disabled,
  className,
  helperText,
  onChange,
  ...props
}, ref) => {
  const [ inputDate, setInputDate ] = useState<any>(null);
  const [ validationError, setValidationError ] = useState<string>('');

  useEffect(
    () => {
      const date = value ? getMoment(value) : null;
      setInputDate(date);
      setValidationError(''); // Clear validation error when value changes externally
    },
    [ value ]
  );

  const updateDate = (momentValue: moment.Moment) => {
    const initialMoment = moment(value || undefined);
    const { years, months, date } = momentValue.toObject();

    return initialMoment.set({ years, months, date }).utc().toISOString();
  };

  const handleChange = (inputMoment: moment.Moment | null) => {
    setInputDate(inputMoment);

    if (!onChange) return;

    // Clear any previous validation errors
    setValidationError('');

    // Validate against min/max dates
    if (inputMoment && inputMoment.isValid()) {
      const minDateMoment = moment(minDate || '1900-01-01T00:00:00Z');
      const maxDateMoment = maxDate ? moment(maxDate) : undefined;
      // Reject dates outside the allowed range
      if (inputMoment.isBefore(minDateMoment, 'day')) {
        const errorMsg = `Date must be on or after ${minDateMoment.format('MM/DD/YYYY')}`;
        setValidationError(errorMsg);
        return; // Don't update the value if before minDate
      }
      if (maxDateMoment && inputMoment.isAfter(maxDateMoment, 'day')) {
        const errorMsg = `Date must be on or before ${maxDateMoment.format('MM/DD/YYYY')}`;
        setValidationError(errorMsg);
        return; // Don't update the value if after maxDate
      }
    }

    const updateValue = inputMoment ? updateDate(inputMoment) : null;

    onChange({ target: { value: updateValue, name } });
  };

  return (
    <Box
      ref={ref}
      className={className}
      sx={{
        '& > div': {
          width: '100%',
        },
        '& div.MuiOutlinedInput-root': {
          marginTop    : '30px !important',
          background   : 'rgb(231, 239, 255) !important',
          border       : '1px solid transparent !important',
          paddingRight : '15px !important',
          overflow     : 'visible',
        },
        '& .MuiInputAdornment-root': {
          display    : 'flex !important',
          marginLeft : 0,
          position   : 'relative',
          zIndex     : 1,
          visibility : 'visible !important',
          opacity    : '1 !important',
        },
        '& .MuiIconButton-root': {
          display    : 'inline-flex !important',
          padding    : '8px',
          visibility : 'visible !important',
          opacity    : '1 !important',
          width      : 'auto !important',
          height     : 'auto !important',
          minWidth   : '40px',
          minHeight  : '40px',
        },
        '& .MuiIconButton-root svg': {
          display    : 'block !important',
          visibility : 'visible !important',
          opacity    : '1 !important',
          width      : '18px !important',
          height     : '19px !important',
        },
      }}
    >
      <DesktopDatePicker
        label={label}
        value={inputDate}
        format="MM/DD/YYYY"
        minDate={moment(minDate || '1900-01-01T00:00:00Z')}
        maxDate={maxDate ? moment(maxDate) : undefined}
        disabled={disabled}
        slots={{
          openPickerIcon: IconDate,
        }}
        slotProps={{
          textField: {
            error           : !!error || !!validationError,
            helperText      : validationError || helperText,
            placeholder     : 'mm/dd/yyyy',
            InputLabelProps : { shrink },
            ...props,
          } as any,
          openPickerButton: {
            'aria-label': 'Date Picker',
          },
        }}
        onChange={handleChange}
      />
    </Box>
  );
};

export const SelectDate = memo(forwardRef(SelectDateBase));
